import { useEffect, useState } from 'react';
import { Mail, KeyRound, Save, Eye, EyeOff } from 'lucide-react';
import { useToastStore } from '../store';
import { Spinner } from '../components/ui';
import { api } from '../utils/api';

export function UserSettingsPage() {
  const { addToast } = useToastStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState({
    email_notifications_enabled: true,
    email_shift_assigned: true,
    email_shift_reminder: true,
    email_request_status: true,
    email_shift_reminder_night_before: true,
    email_shift_reminder_2hours_before: true,
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const result = await api.getUserSettings();
      if (result.success && result.data) {
        setSettings({
          email_notifications_enabled: result.data.email_notifications_enabled === 1,
          email_shift_assigned: result.data.email_shift_assigned === 1,
          email_shift_reminder: result.data.email_shift_reminder === 1,
          email_request_status: result.data.email_request_status === 1,
          email_shift_reminder_night_before: result.data.email_shift_reminder_night_before === 1,
          email_shift_reminder_2hours_before: result.data.email_shift_reminder_2hours_before === 1,
        });
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      addToast('Failed to load settings', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await api.updateUserSettings(settings);
      if (result.success) {
        addToast('Settings saved successfully', 'success');
      } else {
        addToast(result.error || 'Failed to save settings', 'error');
      }
    } catch (error) {
      addToast('Failed to save settings', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      addToast('Please fill in all password fields', 'warning');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      addToast('New password must be at least 6 characters', 'warning');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      addToast('New passwords do not match', 'warning');
      return;
    }

    setIsChangingPassword(true);
    try {
      const result = await api.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      if (result.success) {
        addToast('Password changed successfully', 'success');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        addToast(result.error || 'Failed to change password', 'error');
      }
    } catch (error) {
      addToast('Failed to change password', 'error');
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account settings and preferences</p>
      </div>

      {/* Email Notifications */}
      <div className="card mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-slate-900">Email Notifications</h2>
        </div>
        
        <div className="space-y-3">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.email_notifications_enabled}
              onChange={(e) => setSettings({ ...settings, email_notifications_enabled: e.target.checked })}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <span>Enable all email notifications</span>
          </label>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.email_shift_assigned}
              onChange={(e) => setSettings({ ...settings, email_shift_assigned: e.target.checked })}
              disabled={!settings.email_notifications_enabled}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <span>Send email when shift is assigned</span>
          </label>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.email_shift_reminder}
              onChange={(e) => setSettings({ ...settings, email_shift_reminder: e.target.checked })}
              disabled={!settings.email_notifications_enabled}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <span>Send email reminders for shifts</span>
          </label>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.email_request_status}
              onChange={(e) => setSettings({ ...settings, email_request_status: e.target.checked })}
              disabled={!settings.email_notifications_enabled}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <span>Send email when request status changes</span>
          </label>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.email_shift_reminder_night_before}
              onChange={(e) => setSettings({ ...settings, email_shift_reminder_night_before: e.target.checked })}
              disabled={!settings.email_notifications_enabled}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <span>Send reminder email the night before shift</span>
          </label>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.email_shift_reminder_2hours_before}
              onChange={(e) => setSettings({ ...settings, email_shift_reminder_2hours_before: e.target.checked })}
              disabled={!settings.email_notifications_enabled}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <span>Send reminder email 2 hours before shift</span>
          </label>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="btn btn-primary"
          >
            {isSaving ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Preferences
              </>
            )}
          </button>
        </div>
      </div>

      {/* Change Password */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-slate-900">Change Password</h2>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="input-label">Current Password</label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                className="input pr-10"
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="input-label">New Password</label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                className="input pr-10"
                placeholder="Enter new password (min 6 characters)"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="input-label">Confirm New Password</label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                className="input pr-10"
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleChangePassword}
              disabled={isChangingPassword}
              className="btn btn-primary"
            >
              {isChangingPassword ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <KeyRound className="w-4 h-4" />
                  Change Password
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

